import { StringHelpers } from "../../helpers/string-helpers";
import { IChartContainer } from "../../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../../models/chart-style/chart-style-settings.model";
import { TradeOrderDataModel } from "../../models/draw-trade-order/trade-order-data.model";
import { CurrentPositionViewModel } from "../../models/pnl/current-position-view-model";
import { QuoteType, TradeModel, TradeOrderType } from "../../models/trade/trade.model";
import { IComponent } from "../component.interface";
import { InformationPopupComponent } from "../information-popup.component";

export class QuickOrdersComponent implements IComponent {
    public id = '';

    private element: HTMLElement;
    private base: IChartContainer;

    private currentPositionsViewModel: Array<CurrentPositionViewModel> = [];
    private tradeOrders: Array<TradeOrderDataModel> = [];

    private lastPrice = 0;
    private selectedAccount = '';
    private positionClosed = false;

    constructor(element: HTMLElement, base: IChartContainer) {
        this.id = StringHelpers.newGuid();
        this.element = element;
        this.element.classList.add('hidden');
        this.base = base;
        this.onTradeOptionsClick();
        this.subscribeChartData();
    }

    public updateSelectedAccount(value: string, positionClosed: boolean) {
        this.selectedAccount = value;;
        this.positionClosed = positionClosed;
        this.toggleTradeOptionButtons();
    }

    private toggleTradeOptionButtons() {
        if (this.selectedAccount && !this.positionClosed) {
            this.element.querySelectorAll('button').forEach(el => {
                el.removeAttribute('disabled');
            });
        } else {
            this.element.querySelectorAll('button').forEach(el => {
                el.setAttribute('disabled', 'disabled');
            });
        }
    }

    private onTradeOptionsClick() {
        this.element.querySelectorAll('button').forEach(el => {
            el.addEventListener('click', () => {
                if (el.getAttribute('disabled') != 'disabled') {
                    if (el.classList.contains('flatten')) {
                        this.flatten();
                    } else if (el.classList.contains('one-half')) {
                        this.half();
                    } else if (el.classList.contains('double')) {
                        this.double();
                    } else if (el.classList.contains('reverse')) {
                        this.reverse();
                    }
                }
            })
        });
    }

    private flatten() {
        const position = this.currentPositionsViewModel.find(x => x.accountName == this.selectedAccount);
        if (!position || position.shares == 0) return;

        const existingOrder = this.tradeOrders.some(x => x.accountName == this.selectedAccount &&
            ((position.shares > 0 && !x.isLong) || (position.shares < 0 && x.isLong)) &&
            !x.goodAfterTime &&
            x.tradeType?.toLowerCase() == 'market' &&
            x.statusText?.toLowerCase() == 'submitted')

        const isLong = position.shares >= 0;
        const tradeIsBuy = !isLong;
        const quoteQuantity = Math.abs(position.shares);
        const submitOrder = () => {
            const tradeModel = new TradeModel({
                price: this.lastPrice,
                isBuy: tradeIsBuy,
                orderType: TradeOrderType.Market,
                quoteType: QuoteType.Shares,
                account: position.accountName,
                quoteQuantity: quoteQuantity
            });

            this.base.chartService.requestManualTrade(tradeModel);
        };

        const shouldDisplayOrderConfirmationPopup = this.base.tiChartSeries.chartStyleSettings.chartSettingsPopupModel.displayOrderConfirmationPopup;
        if (shouldDisplayOrderConfirmationPopup){
            const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
            if (existingOrder) {
                const message = `There is an existing unfilled exit order for ${position.symbol}.  Are you sure you want to enter another one?`;
                popup.show(`Flatten Position`, message, () => {
                    submitOrder();
                });
            } else {
                const message = this.getFlattenMessage(tradeIsBuy, quoteQuantity, position);
                popup.show(`Flatten Position`, message, () => {
                    submitOrder();
                });
            }
        } else {
            submitOrder();
        }


    }

    private getFlattenMessage(tradeIsBuy: boolean, shares: number, position: CurrentPositionViewModel): string {
        let html = ``;
        if (!tradeIsBuy) {
            html = `<span class="b red">Sell</span> <span class="b">${Math.abs(shares)} Shares</span> of <span class="b">${position.symbol}</span> at <span class="b">Market,</span><br/>to Flatten (Close) this Position in <span class="b">${position.accountName}</span>?`;
        } else {
            html = `<span class="b green">Buy</span> <span class="b">${Math.abs(shares)} Shares</span> of <span class="b">${position.symbol}</span> at <span class="b">Market,</span><br/>to Flatten (Close) this Position in <span class="b">${position.accountName}</span>?`;
        }

        return `<span class="trade-message">${html}</span>`;
    }

    private half() {
        const position = this.currentPositionsViewModel.find(x => x.accountName == this.selectedAccount);
        if (!position || position.shares == 0) return;

        const currentShares = Math.abs(position.shares);
        const shouldFlatten = currentShares >= 0 && currentShares <= 1;
        const halfShares = shouldFlatten ? currentShares : Math.floor(currentShares / 2);
        const existingOrder = this.tradeOrders.some(x => x.accountName == this.selectedAccount &&
            x.shares == halfShares &&
            !x.goodAfterTime &&
            x.tradeType?.toLowerCase() == 'market' &&
            x.statusText?.toLowerCase() == 'submitted')

        const isLong = position.shares >= 0;
        const tradeIsBuy = !isLong;
        const quoteQuantity = halfShares;
        const submitOrder = () => {
            const tradeModel = new TradeModel({
                price: this.lastPrice,
                isBuy: tradeIsBuy,
                orderType: TradeOrderType.Market,
                quoteType: QuoteType.Shares,
                account: position.accountName,
                quoteQuantity: quoteQuantity
            });

            this.base.chartService.requestManualTrade(tradeModel);
        };

        const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
        const shouldDisplayPopup = this.base.tiChartSeries.chartStyleSettings.chartSettingsPopupModel.displayOrderConfirmationPopup;
        if (existingOrder) {
            const popupMessage = `There is an existing unfilled exit order for shares ${halfShares} of ${position.symbol}.  Are you sure you want to enter another one?`;
            this.showPopupOrSubmit(shouldDisplayPopup, `1/2 Position`, popupMessage, () => {
                submitOrder();
            });
        } else {
            if (shouldFlatten) {
                const popupMessage = this.getFlattenMessage(tradeIsBuy, quoteQuantity, position);
                this.showPopupOrSubmit(shouldDisplayPopup, `Flatten Position`, popupMessage, () => {
                    submitOrder();
                });
            } else {
                const popupMessage = this.getHalfMessage(tradeIsBuy, quoteQuantity, position);
                this.showPopupOrSubmit(shouldDisplayPopup, `1/2 Position`, popupMessage, () => {
                    submitOrder();
                });
            }
        }
    }

    private showPopupOrSubmit(shouldDisplayPopup: boolean, popupTitle: string, popupMessage: string, submitCallback: () => void) {
        const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
        if (shouldDisplayPopup) {
            popup.show(popupTitle, popupMessage, submitCallback);
        } else {
            submitCallback();
        }
    }

    private getHalfMessage(tradeIsBuy: boolean, shares: number, position: CurrentPositionViewModel): string {
        let html = ``;
        if (!tradeIsBuy) {
            html = `<span class="b red">Sell</span> <span class="b">${Math.abs(shares)} Shares</span> of <span class="b">${position.symbol}</span> at <span class="b">Market,</span><br/>to 1/2 (Half) this Position in <span class="b">${position.accountName}</span>?`;
        } else {
            html = `<span class="b green">Buy</span> <span class="b">${Math.abs(shares)} Shares</span> of <span class="b">${position.symbol}</span> at <span class="b">Market,</span><br/>to 1/2 (Half) this Position in <span class="b">${position.accountName}</span>?`;
        }

        return `<span class="trade-message">${html}</span>`;
    }

    private double() {
        const position = this.currentPositionsViewModel.find(x => x.accountName == this.selectedAccount);
        if (!position || position.shares == 0) return;

        const doubleShares = Math.abs(position.shares);
        const existingOrder = this.tradeOrders.some(x => x.accountName == this.selectedAccount &&
            x.shares == doubleShares &&
            !x.goodAfterTime &&
            x.tradeType?.toLowerCase() == 'market' &&
            x.statusText?.toLowerCase() == 'submitted')

        const isLong = position.shares >= 0;
        const tradeIsBuy = isLong;
        const quoteQuantity = doubleShares;
        const submitOrder = () => {
            const tradeModel = new TradeModel({
                price: this.lastPrice,
                isBuy: tradeIsBuy,
                orderType: TradeOrderType.Market,
                quoteType: QuoteType.Shares,
                account: position.accountName,
                quoteQuantity: quoteQuantity
            });

            this.base.chartService.requestManualTrade(tradeModel);
        };

        const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
        if (existingOrder) {
            const message = `There is an existing unfilled exit order for shares ${doubleShares} of ${position.symbol}.  Are you sure you want to enter another one?`;
            popup.show(`Double Position`, message, () => {
                submitOrder();
            });
        } else {
            const message = this.getDoubleMessage(tradeIsBuy, quoteQuantity, position);
            popup.show(`Double Position`, message, () => {
                submitOrder();
            });
        }
    }

    private getDoubleMessage(tradeIsBuy: boolean, shares: number, position: CurrentPositionViewModel): string {
        let html = ``;
        if (!tradeIsBuy) {
            html = `<span class="b red">Sell</span> <span class="b">${Math.abs(shares)} Shares</span> of <span class="b">${position.symbol}</span> at <span class="b">Market,</span><br/>to Double this Position in <span class="b">${position.accountName}</span>?`;
        } else {
            html = `<span class="b green">Buy</span> <span class="b">${Math.abs(shares)} Shares</span> of <span class="b">${position.symbol}</span> at <span class="b">Market,</span><br/>to Double this Position in <span class="b">${position.accountName}</span>?`;
        }

        return `<span class="trade-message">${html}</span>`;
    }

    private reverse() {
        const position = this.currentPositionsViewModel.find(x => x.accountName == this.selectedAccount);
        if (!position || position.shares == 0) return;

        const reverseShares = Math.abs(position.shares) * 2;
        const existingOrder = this.tradeOrders.some(x => x.accountName == this.selectedAccount &&
            x.shares == reverseShares &&
            !x.goodAfterTime &&
            x.tradeType?.toLowerCase() == 'market' &&
            x.statusText?.toLowerCase() == 'submitted')

        const isLong = position.shares >= 0;
        const tradeIsBuy = !isLong;
        const quoteQuantity = reverseShares;
        const submitOrder = () => {
            const tradeModel = new TradeModel({
                price: this.lastPrice,
                isBuy: tradeIsBuy,
                orderType: TradeOrderType.Market,
                quoteType: QuoteType.Shares,
                account: position.accountName,
                quoteQuantity: quoteQuantity
            });

            this.base.chartService.requestManualTrade(tradeModel);
        };

        const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
        if (existingOrder) {
            const message = `There is an existing unfilled exit order for shares ${reverseShares} of ${position.symbol}.  Are you sure you want to enter another one?`;
            popup.show(`Reverse Position`, message, () => {
                submitOrder();
            });
        } else {
            const message = this.getReverseMessage(tradeIsBuy, quoteQuantity, position);
            popup.show(`Reverse Position`, message, () => {
                submitOrder();
            });
        }
    }

    private getReverseMessage(tradeIsBuy: boolean, shares: number, position: CurrentPositionViewModel): string {
        let html = ``;
        if (!tradeIsBuy) {
            html = `<span class="b red">Sell</span> <span class="b">${Math.abs(shares)} Shares</span> of <span class="b">${position.symbol}</span> at <span class="b">Market,</span><br/>to Reverse this Position in <span class="b">${position.accountName}</span>?`;
        } else {
            html = `<span class="b green">Buy</span> <span class="b">${Math.abs(shares)} Shares</span> of <span class="b">${position.symbol}</span> at <span class="b">Market,</span><br/>to Reverse this Position in <span class="b">${position.accountName}</span>?`;
        }

        return `<span class="trade-message">${html}</span>`;
    }

    private subscribeChartData() {
        this.base.chartService.subscribeCurrentPositionViewModel((position: Array<CurrentPositionViewModel>) => {
            this.currentPositionsViewModel = position;
        });
        this.base.chartService.subscribeDrawTradeOrdersData((tradeOrders: Array<TradeOrderDataModel>) => {
            this.tradeOrders = tradeOrders;
        });
        this.base.tiChartSeries?.subscribeLastPriceChange(this.id, (lastPrice: number) => {
            this.lastPrice = lastPrice;
        }); 
        this.base.chartService.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            if (chartStyle.webBasedIndicatorManagerSupported) {
                this.element.classList.remove('hidden');
            }
        });
    }
}